// src/constants/breakpoints.ts

/**
 * BREAKPOINTS - Single Source of Truth
 *
 * This file contains all breakpoint definitions used across the application.
 * Update values here and they will be used throughout TypeScript/JavaScript code.
 *
 * IMPORTANT: After updating values here, also update src/styles/_variables.scss
 * to keep SCSS variables in sync. Consider using the generateSCSSVariables()
 * function below to generate the SCSS code.
 */

// Base breakpoint values in pixels (no 'px' suffix for TypeScript usage)
export const BREAKPOINT_VALUES = {
  XS: 380,
  XSMALL: 480,
  MOBILE: 600,
  TABLET: 768, // Fixed: was 786 in constants/index.ts
  V_TABLET: 1024, // Added: was missing from constants/index.ts
  DESKTOP: 1280,
  WIDE_SCREEN: 1440,
  EXTRA_LARGE_DESKTOP: 1600,
  ULTRAWIDE: 1850,
  XXL: 1400, // Custom breakpoint for carousels
  FULLHD: 2500,
} as const

// Semantic names mapping for playlist configurations
// These map to the column configurations in defaults.ts.
// Values are the min-width where each semantic column key starts applying
// (see getBreakpoints in src/utils/index.ts for the bucket table).
export const SEMANTIC_BREAKPOINT_MAP = {
  base: 0,
  xs: BREAKPOINT_VALUES.XS,
  mobile: BREAKPOINT_VALUES.XSMALL,
  tablet: BREAKPOINT_VALUES.TABLET,
  vtablet: BREAKPOINT_VALUES.V_TABLET,
  desktop: BREAKPOINT_VALUES.DESKTOP,
  widescreen: BREAKPOINT_VALUES.WIDE_SCREEN,
  xxl: BREAKPOINT_VALUES.XXL,
  xxxl: BREAKPOINT_VALUES.EXTRA_LARGE_DESKTOP,
  ultrawidescreen: BREAKPOINT_VALUES.ULTRAWIDE,
  fullhd: BREAKPOINT_VALUES.FULLHD,
} as const

// Swiper breakpoint configuration (sorted descending for Swiper.js)
// These are the exact values used in getBreakpoints function
export const SWIPER_BREAKPOINTS = {
  FULLHD: BREAKPOINT_VALUES.FULLHD,
  ULTRAWIDE: BREAKPOINT_VALUES.ULTRAWIDE,
  XXXL: BREAKPOINT_VALUES.EXTRA_LARGE_DESKTOP,
  XXL: BREAKPOINT_VALUES.XXL,
  WIDE_SCREEN: BREAKPOINT_VALUES.WIDE_SCREEN,
  DESKTOP: BREAKPOINT_VALUES.DESKTOP,
  TABLET: BREAKPOINT_VALUES.TABLET,
  V_TABLET: BREAKPOINT_VALUES.V_TABLET,
  CUSTOM_576: 576, // Custom breakpoint for specific carousel behavior
  XSMALL: BREAKPOINT_VALUES.XSMALL,
  XS: BREAKPOINT_VALUES.XS,
  BASE: 0,
} as const

// Export for backward compatibility with existing code
export const BREAKPOINTS = BREAKPOINT_VALUES